import {noop} from '@benchling/js-utils';
import {StyleSheet, css} from 'aphrodite';
import moment from 'moment';
import React from 'react';

import {
  getArchivedBannerMessage,
  getStepLimitWarningText,
  PUBLISHED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT,
} from 'analysis-tool/AnalysisToolMessages';
import RunAnalysisTemplateModal, {
  RunAnalysisTemplateModalProps,
} from 'analysis-tool/create-analysis-modal/RunAnalysisTemplateModal';
import {refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import {AnalysisRightSidebarItem} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {
  copyAnalysisTemplate,
  unarchiveAnalysisTemplate,
  updateAnalysisTemplateMetadata,
} from 'analysis-tool/templates/AnalysisTemplateActions';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import DuplicateAnalysisTemplateModal, {
  DuplicateAnalysisTemplateModalProps,
} from 'analysis-tool/templates/DuplicateAnalysisTemplateModal';
import PublishAnalysisTemplateDropdown from 'analysis-tool/templates/PublishAnalysisTemplateDropdown';
import BaseAnalysisUIHeader from 'analysis-tool/ui/header/BaseAnalysisUIHeader';
import {AnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import AnalysisTemplateMetadataModal from 'analysis-tool/ui/metadata-modal/AnalysisTemplateMetadataModal';
import AnalysisTemplateCompatibilityModal from 'analysis-tool/ui/metadata-modal/compatibility/AnalysisTemplateCompatibilityModal';
import {BulkArchiveFilesOptions} from 'archive/ArchiveActions';
import {getArchiveAction} from 'archive/ArchiveActionsUtil';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {MoveToTemplateCollectionModal} from 'notebook/entry-templates/collections/MoveToTemplateCollectionModal';
import TemplateCollectionDAO from 'notebook/entry-templates/collections/TemplateCollectionDAO';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import Badge, {BadgeColor} from 'ui-library/Badge';
import {InlineBannerActionType, InlineBannerProps, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {BreadcrumbItem} from 'ui-library/Breadcrumb';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import {OverflowMenuOption} from 'ui-library/menus/OverflowMenu';
import runModal from 'ui-library/runModal';
import * as DateUtil from 'util/DateUtil';
import {withLoadingScreen} from 'util/Promises';
import removeNulls from 'util/removeNulls';

function getRunAutomationTemplateDisabledTooltip(analysisTemplate: AnalysisTemplateGraphQL): string | null {
  if (analysisTemplate.publishedAt == null) {
    return 'Draft automation templates cannot be run.';
  }
  if (analysisTemplate.dataFrameVariables.length > 0) {
    return (
      'Automation templates with variable tables cannot be run directly. ' +
      'To use this template, first create an analysis.'
    );
  }
  return null;
}

function getOverflowMenuOptions(analysisTemplate: AnalysisTemplateGraphQL): Array<OverflowMenuOption> {
  const runAutomationTemplateTooltipTitle = getRunAutomationTemplateDisabledTooltip(analysisTemplate);
  return removeNulls([
    {
      icon: 'copy',
      contents: <span>Duplicate</span>,
      onSelect: () => {
        legacyRunModal<DuplicateAnalysisTemplateModalProps>(DuplicateAnalysisTemplateModal, {
          sourceAnalysisTemplateName: analysisTemplate.name,
          sourceAnalysisTemplateCollectionId: analysisTemplate.templateCollection.id,
          sourceTemplateIsPublished: analysisTemplate.publishedAt != null,
          sourceTemplateDescription: analysisTemplate.description,
          actionOrigin: ActionOrigin.ANALYSIS_TOOL_HEADER,
          onRequestClose: noop,
          onRequestSubmit: async (params) => {
            await withLoadingScreen(
              copyAnalysisTemplate({
                name: params.name,
                copiedFromAnalysisTemplateId: analysisTemplate.id,
                templateCollectionId: params.templateCollectionId,
                description: params.description,
              })
            );
          },
        });
      },
    },
    {
      icon: 'info-circle',
      contents: <span>View metadata</span>,
      onSelect: () => {
        runModal(
          AnalysisTemplateMetadataModal,
          {analysisTemplate},
          // Render at the legacy modal z-index (`ZINDEX_MODAL`, 1050) rather than the default `runModal`
          // z-index (`ZINDEX_FLOATING_UI`, 1060). TemplateCollectionSelector renders its dropdown on
          // `document.body` via AbsoluteDropdownContainer at z-index 1051, which would otherwise be
          // hidden behind (and unclickable through) this modal's overlay. See openAddToEntryModal for
          // the same pattern.
          {title: 'Automation template metadata', size: 'lg', zIndex: ZINDEX_MODAL}
        );
      },
      dataTestAttrs: {component: 'AnalysisTemplateUIHeader', element: 'viewMetadataOption'},
    },
    {
      icon: 'check-circle-outline',
      contents: <span>Check compatibility</span>,
      onSelect: () => {
        runModal(
          AnalysisTemplateCompatibilityModal,
          {analysisTemplate},
          {title: 'Check compatibility', size: 'lg'}
        );
      },
      dataTestAttrs: {component: 'AnalysisTemplateUIHeader', element: 'checkCompatibilityOption'},
    },
    {
      icon: 'audit-trail',
      contents: <span>{EXPORT_AUDIT_LOG_DISPLAY_STRING}</span>,
      onSelect: () => {
        exportAuditLogs([analysisTemplate.id]);
      },
    },
    analysisTemplate.permissions.archivable && !isArchived(analysisTemplate)
      ? {
          icon: 'archive',
          contents: <span>Archive template</span>,
          onSelect: () => {
            ArchiveModal.open({
              items: [analysisTemplate],
              onArchive: (
                _templateIds: Array<string>,
                purpose: string,
                options: BulkArchiveFilesOptions
              ): void => {
                getArchiveAction([analysisTemplate.id])([analysisTemplate.id], purpose, options, () => {
                  refreshAnalysisTemplate(analysisTemplate.id);
                });
              },
            });
          },
        }
      : null,
    analysisTemplate.permissions.writable && analysisTemplate.permissions.movable
      ? {
          icon: 'move',
          contents: <span>Move template</span>,
          onSelect: () => {
            legacyRunModal(MoveToTemplateCollectionModal, {
              title: 'Move template',
              onRequestSubmit: async (newTemplateCollectionId: string) => {
                await withLoadingScreen(
                  updateAnalysisTemplateMetadata(analysisTemplate, {
                    templateCollectionId: newTemplateCollectionId,
                  })
                );
              },
              onRequestClose: noop,
              initialTemplateCollectionId: analysisTemplate.templateCollection.id,
            });
          },
        }
      : null,
    {
      icon: 'lightbulb',
      contents: <span>Run automation template</span>,
      isDisabled: runAutomationTemplateTooltipTitle != null,
      tooltipTitle: runAutomationTemplateTooltipTitle,
      onSelect: () => {
        legacyRunModal<RunAnalysisTemplateModalProps>(RunAnalysisTemplateModal, {
          initialTemplate: analysisTemplate,
          initialAnalysisName: `${analysisTemplate.name} ${DateUtil.readableTime(moment(), {
            showTime: true,
          })}`,
          actionOrigin: ActionOrigin.ANALYSIS_TOOL_HEADER,
        });
      },
    },
  ]);
}

function getBannerStackMessages(analysisTemplate: AnalysisTemplateGraphQL): Array<InlineBannerProps> {
  const messages: Array<InlineBannerProps> = [];
  if (isArchived(analysisTemplate)) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'box-archive',
      action: analysisTemplate.permissions.unarchivable
        ? {
            actionType: InlineBannerActionType.CUSTOM,
            actionText: 'Unarchive',
            actionOnClick: () => {
              unarchiveAnalysisTemplate(analysisTemplate.id);
            },
          }
        : undefined,
      text: getArchivedBannerMessage(analysisTemplate, analysisTemplate.archiveRecord.purpose),
      dataTestAttrs: {component: 'ArchivedBanner', element: 'root'},
    });
  } else if (analysisTemplate.publishedAt != null) {
    messages.push({
      bannerType: InlineBannerType.INFO,
      text: PUBLISHED_ANALYSIS_TEMPLATE_TOOLTIP_TEXT,
      dataTestAttrs: {component: 'PublishedBanner', element: 'root'},
    });
  } else if (analysisTemplate.steps.length >= BenchlingConfig.PIPELINES_MAX_STEP_COUNT * 0.9) {
    messages.push({
      bannerType: InlineBannerType.WARNING,
      icon: 'exclamation-triangle',
      text: getStepLimitWarningText(analysisTemplate, analysisTemplate.steps.length),
    });
  }
  return messages;
}

function getAnalysisTemplateBreadcrumbInfo(analysisTemplate: AnalysisTemplateGraphQL): {
  items: Array<BreadcrumbItem>;
  icon: BenchlingIconName;
} {
  return {
    items: [
      {
        label: analysisTemplate.templateCollection.name,
        href: TemplateCollectionDAO.collectionURL(analysisTemplate.templateCollection.id),
      },
    ],
    icon: 'template_collection',
  };
}

export interface AnalysisTemplateUIHeaderProps {
  analysisTemplate: AnalysisTemplateGraphQL;
  showOpenSidebarButton: boolean;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem) => void;
  onOpenRightSidebarItem?: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onOpenSidebar: () => void;
  onOpenStepInitializationView: (newSelectedItem: AnalysisMainViewItem) => void;
}

const styles = StyleSheet.create({
  badgeWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    gap: 12,
  },
});

export default function AnalysisTemplateUIHeader({
  analysisTemplate,
  showOpenSidebarButton,
  onChangeSelectedItem,
  onOpenRightSidebarItem,
  onOpenSidebar,
  onOpenStepInitializationView,
}: AnalysisTemplateUIHeaderProps): JSX.Element {
  const overflowMenuOptions = getOverflowMenuOptions(analysisTemplate);
  const bannerStackMessages = getBannerStackMessages(analysisTemplate);
  const breadcrumbInfo = getAnalysisTemplateBreadcrumbInfo(analysisTemplate);

  // TODO(BNCH-148502): Allow renaming analysis templates from <AnalysisTemplateUIHeader>
  return (
    <BaseAnalysisUIHeader
      pipeline={analysisTemplate}
      showOpenSidebarButton={showOpenSidebarButton}
      onChangeSelectedItem={onChangeSelectedItem}
      onOpenRightSidebarItem={onOpenRightSidebarItem}
      onOpenSidebar={onOpenSidebar}
      onOpenStepInitializationView={onOpenStepInitializationView}
      overflowMenuOptions={overflowMenuOptions}
      bannerStackMessages={bannerStackMessages}
      breadcrumbInfo={breadcrumbInfo}
    >
      <div className={css(styles.badgeWrapper)}>
        <Badge startingIcon={ModelTypeConstants.TEMPLATE_COLLECTION} color={BadgeColor.COLLECTIBLE_BLUE}>
          Template
        </Badge>
        <PublishAnalysisTemplateDropdown analysisTemplate={analysisTemplate} />
      </div>
    </BaseAnalysisUIHeader>
  );
}
